import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  Activity,
  Receipt,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { cn } from "./cn";
import { UiLink } from "./router";

export type NotificationKind = "device_approval" | "security" | "wallet" | "usage";

export type NotificationItem = {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  /**
   * Display-ready relative time, e.g. "2 hrs ago". The design formatted an ISO
   * string against a frozen `NOW` constant from its fixture module; a
   * presentational shell has neither a fixture nor a clock, so the screen that
   * owns the data formats it.
   */
  at: string;
  /** Where the notification takes you when opened. */
  href: string;
  read: boolean;
};

const kinds: Record<
  NotificationKind,
  { icon: typeof Bell; tone: string; label: string }
> = {
  device_approval: {
    icon: ShieldCheck,
    tone: "bg-warning-soft text-warning-ink",
    label: "Device approval",
  },
  security: {
    icon: ShieldAlert,
    tone: "bg-danger-soft text-danger-ink",
    label: "Security",
  },
  wallet: { icon: Receipt, tone: "bg-success-soft text-success-ink", label: "Wallet" },
  usage: { icon: Activity, tone: "bg-brand-soft text-brand-ink", label: "Usage" },
};

export function Notifications({ items }: { items: NotificationItem[] }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  /*
   * Marking read is an overlay on the props, not a copy of them. The design
   * seeded `useState(items)` from a module fixture that never changed; here
   * `items` is refetched, and a seeded copy would pin the panel to whatever
   * the first render happened to see. Reading is local-only either way —
   * nothing persists it yet (see the deferred backlog).
   */
  const [readLocally, setReadLocally] = useState<ReadonlySet<string>>(new Set());
  const list = useMemo(
    () => items.map((item) => ({ ...item, read: item.read || readLocally.has(item.id) })),
    [items, readLocally],
  );

  const unread = list.filter((item) => !item.read).length;

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function markAllRead() {
    setReadLocally(new Set(items.map((item) => item.id)));
  }

  function markRead(id: string) {
    setReadLocally((current) => new Set(current).add(id));
  }

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label={
          unread ? `Notifications, ${unread} unread` : "Notifications, none unread"
        }
        className={cn(
          "relative grid size-9 place-items-center rounded-field transition-colors",
          open ? "bg-surface-sunken text-ink" : "text-ink-2 hover:bg-surface-sunken",
        )}
      >
        <Bell className="size-4" aria-hidden />
        {unread ? (
          <span className="absolute top-1 right-1 grid min-w-4 place-items-center rounded-full bg-warning px-1 text-[10px] leading-4 font-bold text-white ring-2 ring-surface">
            {unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="region"
          aria-label="Notifications"
          className={cn(
            "fixed inset-x-2 top-[4.25rem] z-50 overflow-hidden rounded-card border border-line bg-surface-raised shadow-float",
            "sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-2 sm:w-96",
          )}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
            <h2 className="font-display text-base font-bold text-ink">
              Notifications
              {unread ? (
                <span className="ml-2 text-2xs font-semibold text-ink-4">
                  {unread} unread
                </span>
              ) : null}
            </h2>
            {unread ? (
              <button
                type="button"
                onClick={markAllRead}
                className="text-2xs font-semibold text-brand hover:text-brand-strong"
              >
                Mark all as read
              </button>
            ) : null}
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <span
                aria-hidden
                className="grid size-11 place-items-center rounded-full bg-surface-sunken text-ink-4"
              >
                <BellOff className="size-5" />
              </span>
              <p className="mt-3 text-base font-semibold text-ink">You are all caught up</p>
              <p className="mt-1 text-xs text-ink-4">
                Approvals, top-ups and security events show up here.
              </p>
            </div>
          ) : (
            <ul className="max-h-[min(28rem,60vh)] overflow-y-auto scrollbar-slim">
              {list.map((item) => {
                const kind = kinds[item.kind];
                return (
                  <li key={item.id} className="border-b border-line-soft last:border-0">
                    <UiLink
                      href={item.href}
                      onClick={() => {
                        markRead(item.id);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex gap-3 px-4 py-3 transition-colors hover:bg-surface-sunken/70",
                        !item.read && "bg-brand-softer/60",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "mt-0.5 grid size-8 shrink-0 place-items-center rounded-[10px]",
                          kind.tone,
                        )}
                      >
                        <kind.icon className="size-4" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="flex items-start gap-2">
                          <span
                            className={cn(
                              "text-xs text-ink",
                              item.read ? "font-semibold" : "font-bold",
                            )}
                          >
                            {item.title}
                          </span>
                          {!item.read ? (
                            <span
                              aria-label="Unread"
                              className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand"
                            />
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-2xs leading-relaxed text-ink-3">
                          {item.body}
                        </span>
                        <span className="mt-1 block text-2xs text-ink-4">
                          {kind.label} · {item.at}
                        </span>
                      </span>
                    </UiLink>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t border-line-soft bg-surface-sunken/40 px-4 py-2.5 text-center">
            <UiLink
              href="/security"
              onClick={() => setOpen(false)}
              className="text-2xs font-semibold text-brand hover:text-brand-strong"
            >
              Devices and sessions on this account
            </UiLink>
          </div>
        </div>
      ) : null}
    </div>
  );
}
